import {
  Autocomplete,
  Box,
  debounce,
  Grid2 as Grid,
  TextField,
  Typography
} from '@mui/material';
import {
  CustomContact,
  useCustomContactsPaginated
} from 'api/services/BrandPortal/CustomContacts';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isBottomReached } from '../../Select/isBottomReached';
import { contactIsEqual, getContactKey } from './customContactUtils';

type Option =
  | { type: 'HEADER' }
  | { type: 'FROM_REQUEST'; contact: CustomContact }
  | { type: 'PLAIN'; email: string }
  | { type: 'NEW'; value: string };

export const CustomContactsField: React.FC<{
  label: string;
  testId: string;
  brandPortalKey: string;
  onChange: (emails: string[]) => void;
  error?: string;
}> = props => {
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  // needed to avoid the necessity of useCallback for the onChange prop
  const onChangeRef = useRef(props.onChange);
  useEffect(() => {
    onChangeRef.current = props.onChange;
  }, [props.onChange]);

  // inform outer component about changes
  useEffect(() => {
    onChangeRef.current(
      selectedOptions
        .filter(
          option => option.type === 'PLAIN' || option.type === 'FROM_REQUEST'
        )
        .map(option => {
          switch (option.type) {
            case 'PLAIN':
              return option.email;
            case 'FROM_REQUEST':
              return option.contact.email_address;
            default:
              return null;
          }
        })
        .filter((email): email is string => email !== null)
    );
  }, [selectedOptions]);

  const [inputValue, setInputValue] = useState('');

  const {
    options: optionsFromRequest,
    changeFilter,
    loading,
    reachedLastPage,
    fetchNextPage
  } = useCustomContactsPaginated(props.brandPortalKey);

  const debouncedHandleQueryChange = useMemo(
    () => debounce(changeFilter, 500),
    [changeFilter]
  );

  const options = useMemo<Option[]>(() => {
    if (
      inputValue !== '' &&
      !loading &&
      optionsFromRequest?.length === 0 &&
      selectedOptions?.find(
        o =>
          (o.type === 'FROM_REQUEST' &&
            o.contact.email_address === inputValue) ||
          (o.type === 'PLAIN' && o.email === inputValue)
      ) === undefined
    ) {
      return [{ type: 'NEW', value: inputValue }];
    } else {
      const fromRequestOptions: Option[] =
        optionsFromRequest?.map(contact => ({
          type: 'FROM_REQUEST',
          contact
        })) ?? [];
      return [{ type: 'HEADER' }, ...fromRequestOptions];
    }
  }, [inputValue, loading, optionsFromRequest, selectedOptions]);

  return (
    <Autocomplete
      multiple
      renderInput={params => (
        <TextField
          {...params}
          label={props.label}
          placeholder="Type to search, or type and press enter to add"
          margin="normal"
          data-testid={props.testId}
          error={!!props.error}
          helperText={props.error}
        />
      )}
      value={selectedOptions}
      onChange={(_, newValue) =>
        setSelectedOptions(
          newValue.map(option =>
            option.type === 'NEW'
              ? { type: 'PLAIN', email: option.value }
              : option
          )
        )
      }
      onKeyDown={event => {
        if (event.key === 'Enter' && inputValue.trim() !== '') {
          setSelectedOptions(prev => [
            ...prev,
            { type: 'PLAIN', email: inputValue }
          ]);
          setInputValue('');
          debouncedHandleQueryChange('');
        }
      }}
      isOptionEqualToValue={isOptionEqualToValue}
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionKey={getOptionKey}
      loading={loading}
      // we filter by querying the API directly, so we don't need to filter here
      // https://mui.com/material-ui/react-autocomplete/#search-as-you-type
      filterOptions={options => options}
      inputValue={inputValue}
      renderOption={({ key, ...optionProps }, option) => {
        if (option.type === 'HEADER') {
          return (
            <Grid
              component="li"
              container
              spacing={1}
              key={key}
              className={optionProps.className}
              sx={theme => ({
                '&:hover': {
                  cursor: 'default'
                },
                paddingTop: `${theme.spacing(1)}!important`,
                paddingBottom: `${theme.spacing(1)}!important`,
                position: 'sticky',
                background: theme.palette.grey[50],
                borderBottom: `1px solid ${theme.palette.grey[300]}`,
                top: 0
              })}
            >
              <HeaderOption />
            </Grid>
          );
        }
        return (
          <Grid component="li" container spacing={1} key={key} {...optionProps}>
            <OptionContent option={option} inputValue={inputValue} />
          </Grid>
        );
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason !== 'reset') {
          // to avoid the inputValue being reset when the Autocomplete rerenders due to options change
          setInputValue(newInputValue);
          debouncedHandleQueryChange(newInputValue);
        }
      }}
      slotProps={{
        listbox: {
          sx: {
            paddingTop: 0,
            paddingBottom: 0
          },
          onScroll: event => {
            if (
              isBottomReached(event.currentTarget) &&
              !loading &&
              !reachedLastPage
            ) {
              fetchNextPage();
            }
          }
        }
      }}
    />
  );
};

const HeaderOption: React.FC = () => (
  <>
    <Grid size={5}>
      <Typography sx={{ fontWeight: 500 }} variant="body2">
        Email address
      </Typography>
    </Grid>
    <Grid size={3}>
      <Typography sx={{ fontWeight: 500 }} variant="body2">
        Manufacturer
      </Typography>
    </Grid>
    <Grid size={3}>
      <Typography sx={{ fontWeight: 500 }} variant="body2">
        Distributor
      </Typography>
    </Grid>
    <Grid size={1}>
      <Typography sx={{ fontWeight: 500 }} variant="body2">
        Factory
      </Typography>
    </Grid>
  </>
);

const getOptionLabel = (option: Option) => {
  switch (option.type) {
    case 'NEW':
      return option.value;
    case 'FROM_REQUEST':
      return `${option.contact.email_address} (${option.contact.manufacturer})`;
    case 'PLAIN':
      return option.email;
    case 'HEADER':
      return 'Header';
  }
};

const isOptionEqualToValue = (a: Option, b: Option) => {
  if (a.type === 'FROM_REQUEST' && b.type === 'FROM_REQUEST') {
    contactIsEqual(a.contact, b.contact);
  } else if (a.type === 'PLAIN' && b.type === 'PLAIN') {
    return a.email === b.email;
  } else if (a.type === 'NEW' && b.type === 'NEW') {
    return a.value === b.value;
  }
  return false;
};

const getOptionKey = (option: Option) => {
  switch (option.type) {
    case 'NEW':
      return option.value;
    case 'FROM_REQUEST':
      return getContactKey(option.contact);
    case 'PLAIN':
      return option.email;
    case 'HEADER':
      return 'header';
  }
};

const OptionContent: React.FC<{ option: Option; inputValue: string }> = ({
  option,
  inputValue
}) => {
  switch (option.type) {
    case 'NEW':
      return (
        <Box
          sx={{
            color: theme => theme.palette.grey[600], // Or another shade from the palette
            paddingY: 1
          }}
        >
          No results found for &quot;{inputValue}&quot;. Click here or press
          enter to add new contact.
        </Box>
      );
    case 'PLAIN':
      return option.email;
    case 'FROM_REQUEST':
      return (
        <>
          <Grid size={5}>
            <OptionCell>{option.contact.email_address}</OptionCell>
          </Grid>
          <Grid size={3}>
            <OptionCell>{option.contact.manufacturer}</OptionCell>
          </Grid>
          <Grid size={3}>
            <OptionCell>{option.contact.distributor}</OptionCell>
          </Grid>
          <Grid size={1}>
            <OptionCell>{option.contact.factory}</OptionCell>
          </Grid>
        </>
      );
  }
};

const OptionCell: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Typography
    variant="body2"
    sx={{
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }}
  >
    {children}
  </Typography>
);
